// CRM: a integração da marca com o CRM do cliente (hoje HubSpot; Pipedrive e
// Bitrix24 estão no catálogo da tela, mas ainda "em breve"). Duas metades que
// vieram de seções separadas de api.ts e que aqui viram uma só:
//
//   - o CATÁLOGO DE ETAPAS (/v1/crm/stages): os funis e etapas que o portal do
//     cliente conhece, mais as etapas que a marca cadastrou pelo id. É o que
//     alimenta o mapa etapa → status na tela de Regra de fila;
//   - a CONEXÃO (/v1/integrations/:provider/*): as chaves de ingestão, o status
//     do primeiro payload e o token de LEITURA do portal, guardado por cliente.
//
// POR QUE um módulo só: as duas metades são a mesma credencial vista de dois
// lados. É o token de company (`saveCrmCompanyToken`) que faz `getCrmStages`
// devolver funil — sem ele o back responde `pipelinesUnavailable`. Por isso a
// tela de conexão invalida ["crm-stages"] ao salvar e ao remover o token
// (ver src/hooks/use-crm.ts). Separados, cada arquivo ficaria com metade de uma
// explicação e os dois se importariam para contar a história inteira.
//
// O que NÃO está aqui: MCP (./mcp.js). Também é integração com software
// externo, mas de outra natureza — protocolo de agente autenticado por chave
// própria, sem funil, sem etapa e sem ingestão de lead. Nenhum tipo em comum.
import { api, toApiError } from "./client.js";

// --- Catálogo de etapas (/v1/crm/stages) --------------------------------------
// Inventário de funis/etapas que o HubSpot conhece, mais as etapas que a marca
// cadastrou à mão. Morou em api.ts até esta onda, ao lado da telefonia, embora
// quem o consuma seja a tela de Regra de fila e a de conexão com o CRM.
export interface CrmStageOption {
  id: string;
  label: string;
}

export interface CrmPipeline {
  id: string;
  label: string;
  stages: CrmStageOption[];
}

// Uma etapa que a marca conhece por id, independente de o HubSpot responder.
// Alimentada pela própria ingestão (cada `stage_id` que chega) e pelo cadastro
// manual na tela.
export interface CrmKnownStage {
  stageId: string;
  /** O que exibir: nome do gestor > rótulo do CRM > o próprio id. */
  label: string;
  /** Nome dado na tela; null quando ninguém nomeou. É o campo editável. */
  customLabel: string | null;
  /** Rótulo que veio no payload; costuma ser null (o HubSpot não manda). */
  crmLabel: string | null;
  source: string;
  lastSeenAt: string | null;
}

export interface CrmStagesResponse {
  pipelines: CrmPipeline[];
  // Opcional durante rollout: versões anteriores do back não enviam os campos.
  knownStages?: CrmKnownStage[];
  /**
   * O portal não pôde ser consultado (sem token, token de outro portal, API
   * fora). `pipelines` vem vazio — a tela precisa dizer isso, senão parece que
   * a marca não tem etapa nenhuma.
   */
  pipelinesUnavailable?: boolean;
}

// GET /v1/crm/stages — funis e etapas reais do HubSpot (auth de admin por
// sessão), já ordenados pelo displayOrder. É o catálogo que alimenta o
// seletor de etapas por funil: duas etapas de mesmo rótulo em funis
// diferentes só são distinguíveis agrupadas por pipeline.
export async function getCrmStages(): Promise<CrmStagesResponse> {
  const { data, error } = await api<CrmStagesResponse>("/v1/crm/stages");
  if (error) throw toApiError("GET /v1/crm/stages", error);
  return data;
}

// POST /v1/crm/stages — cadastra uma etapa pelo id do CRM. Serve para mapear
// uma etapa ANTES do primeiro lead dela chegar: sem isso, a etapa nova só
// aparece depois que alguém já ficou fora da fila por causa dela.
export async function createCrmStage(input: {
  stageId: string;
  label?: string | null;
}): Promise<{ stage: CrmKnownStage }> {
  const { data, error } = await api<{ stage: CrmKnownStage }>("/v1/crm/stages", {
    method: "POST",
    body: input,
  });
  if (error) throw toApiError("POST /v1/crm/stages", error);
  return data;
}

// PATCH /v1/crm/stages/:stageId — nomeia a etapa. `null` limpa o nome e devolve
// a exibição ao rótulo do CRM (ou ao id).
export async function renameCrmStage(
  stageId: string,
  label: string | null,
): Promise<{ stage: CrmKnownStage }> {
  const { data, error } = await api<{ stage: CrmKnownStage }>(
    `/v1/crm/stages/${encodeURIComponent(stageId)}`,
    { method: "PATCH", body: { label } },
  );
  if (error) throw toApiError("PATCH /v1/crm/stages", error);
  return data;
}

// --- Conexão por CRM (/integrations/crm/:provider) -------------------------
// A chave de ingestão é POR ORG e por CRM: o back resolve a org PELA CHAVE, então
// o payload do CRM não escolhe mais em qual marca o lead cai.
export type IntegrationProvider = "hubspot" | "pipedrive" | "bitrix24";

export interface IntegrationKey {
  id: string;
  name: string;
  provider: string;
  keyPrefix: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export interface IntegrationStatus {
  hasActiveKey: boolean;
  lastKeyUseAt: string | null;
  lastPayloadAt: string | null;
  payloads24h: number;
  rejected24h: number;
}

export async function getIntegrationStatus(
  provider: IntegrationProvider,
): Promise<IntegrationStatus> {
  const path = `/v1/integrations/${provider}/status`;
  const { data, error } = await api<IntegrationStatus>(path);
  if (error) throw toApiError(`GET ${path}`, error);
  return data;
}

// O token de LEITURA do portal, guardado por CLIENTE (company) e cifrado no
// back. A API nunca devolve o valor — só se existe e quando mudou.
//
// POR QUE existe: a lista de funis da tela de Regra de fila era lida com um
// token único do servidor, então marca de um cliente enxergava os funis do
// portal de outro. Sem token da própria company, a tela passa a mostrar apenas
// as etapas que o Disk conhece por id.
export interface CrmCompanyTokenStatus {
  configured: boolean;
  updatedAt: string | null;
}

export async function getCrmCompanyToken(
  provider: IntegrationProvider,
): Promise<CrmCompanyTokenStatus> {
  const path = `/v1/integrations/${provider}/company-token`;
  const { data, error } = await api<CrmCompanyTokenStatus>(path);
  if (error) throw toApiError(`GET ${path}`, error);
  return data;
}

export async function saveCrmCompanyToken(
  provider: IntegrationProvider,
  token: string,
): Promise<CrmCompanyTokenStatus> {
  const path = `/v1/integrations/${provider}/company-token`;
  const { data, error } = await api<CrmCompanyTokenStatus>(path, {
    method: "PUT",
    body: { token },
  });
  if (error) throw toApiError(`PUT ${path}`, error);
  return data;
}

export async function removeCrmCompanyToken(
  provider: IntegrationProvider,
): Promise<{ removed: boolean }> {
  const path = `/v1/integrations/${provider}/company-token`;
  const { data, error } = await api<{ removed: boolean }>(path, { method: "DELETE" });
  if (error) throw toApiError(`DELETE ${path}`, error);
  return data;
}

export async function getIntegrationKeys(provider: IntegrationProvider): Promise<IntegrationKey[]> {
  const path = `/v1/integrations/${provider}/keys`;
  const { data, error } = await api<{ keys: IntegrationKey[] }>(path);
  if (error) throw toApiError(`GET ${path}`, error);
  return data.keys;
}

// O segredo (`key`) só vem AQUI, uma vez — não é recuperável depois.
export async function createIntegrationKey(
  provider: IntegrationProvider,
  input: { name: string },
): Promise<IntegrationKey & { key: string }> {
  const path = `/v1/integrations/${provider}/keys`;
  const { data, error } = await api<IntegrationKey & { key: string }>(path, {
    method: "POST",
    body: input,
  });
  if (error) throw toApiError(`POST ${path}`, error);
  return data;
}

export async function revokeIntegrationKey(
  provider: IntegrationProvider,
  id: string,
): Promise<void> {
  const path = `/v1/integrations/${provider}/keys/${id}`;
  const { error } = await api(path, { method: "DELETE" });
  if (error) throw toApiError(`DELETE ${path}`, error);
}
